import React, { useRef, useMemo } from 'react';
import { createChatStrategy } from '@varos/assistant-react-chat';
import { assert, assertNever } from '@varos/util-typescript';
import { useParams } from 'react-router';
import { ContextItem } from '@varos/assistant-sdk';
import { ReportingShellContainerBaseConfig } from '../../../base';
import { ChatDetailContainerProps, ChatDetailViewProps } from './chatDetailProps';
import {
    ChatContextProvider,
    ChatDetailController,
    ChatDetailLoader,
} from './chatDetailInterface';

export function createChatDetailContainer(
    config: ReportingShellContainerBaseConfig & {
        chat: {
            context: ChatContextProvider;
        };
    },
    loader: ChatDetailLoader,
    controller: ChatDetailController,
    View: React.FC<ChatDetailViewProps>
): React.FC<ChatDetailContainerProps> {
    const {
        repository: { research: repository, assistant },
        context: { useContext },
        pubsub: {
            router: { useHandler, dispatch },
        },
    } = config;

    const { useChat } = createChatStrategy({});

    return config.state.chat.subscribe(({ ...containerProps }) => {
        const context = useContext();
        const params = useParams();
        const state = {
            chat: config.state.chat.useState(context),
            composer: config.state.composer.useState(context),
        };

        const mutation = {
            content: repository.document.useCreate(context),
            suggestion: {
                create: repository.suggestion.useCreate(context),
                update: repository.suggestion.useUpdate(context),
            },
            message: {
                create: assistant.message.useCreate(context),
                update: assistant.message.useUpdate(context),
            },
            thread: {
                update: assistant.thread.useUpdate(context),
            },
        };

        const data = loader.useLoad(context, {
            content: containerProps.content
                ? {
                      id: containerProps.content,
                  }
                : null,
            thread: {
                id: containerProps.thread,
            },
        });

        // TODO encapsulate token access
        const auth = config.hook.useAuth();
        assert(auth.scheme.kind === 'legacy', 'legacy auth scheme is required');
        assert(auth.scheme.store.authToken, 'auth token is required');

        const chat = useChat(context, {
            options: {
                mode: state.composer.value.test ? 'test' : 'live',
                tag: state.composer.value.version,
                workspace: context.workspace.id,
            },
            token: auth.scheme.store.authToken,
            messages: data.messages ?? [],
            async onMessageCreate(message) {
                // console.log('creating message', message);
                assert(data.thread, 'thread is required');
                const response = await mutation.message.create.mutateAsync({
                    id: message.id,
                    thread: data.thread.id,
                    role: message.role,
                    parts: message.parts,
                    context: message.context,
                });
                console.log('message created', response);
            },
            async onMessageUpdate(message) {
                console.log('updating message', message);
                const response = await mutation.message.update.mutateAsync({
                    id: message.id,
                    parts: message.parts,
                });
                console.log('message updated successfully', response);
            },
            async onCreateDocument(args) {
                console.log('creating document', args);
                const response = await mutation.content.mutateAsync({
                    type: 'document',
                    parent: args.parent,
                    title: args.title,
                    icon: args.icon,
                    blocks: args.blocks,
                });

                if (state.composer.value.mode === 'automatic') {
                    console.log('automatic mode opening document', response);
                    dispatch({
                        kind: 'open',
                        data: {
                            content: {
                                id: response.id,
                            },
                        },
                    });
                }
                console.log('document created', response);
                return {
                    id: response.id,
                };
            },
            async onReplaceDocument(args) {
                console.log('replacing document', args);
                dispatch({
                    kind: 'replace',
                    data: {
                        content: {
                            id: args.id,
                        },
                        blocks: args.blocks,
                    },
                });
            },
        });

        const pubsub = useHandler(context, {
            async onEvent(event) {
                console.log('onEvent', event);
                if (event.kind === 'ask') {
                    console.log('ask', event.data);
                    const { contentId } = params;
                    assert(contentId, 'contentId is required');
                    const response = await chat.append({
                        parts: [
                            {
                                type: 'text',
                                text: event.data.text,
                            },
                        ],
                        context: [
                            {
                                kind: 'selection',
                                data: {
                                    document: contentId,
                                    blocks: event.data.selection.blocks.map(
                                        (block) => block.id
                                    ),
                                    start: {
                                        line: event.data.selection.start.lineNumber,
                                        column: event.data.selection.start.column,
                                    },
                                    end: {
                                        line: event.data.selection.end.lineNumber,
                                        column: event.data.selection.end.column,
                                    },
                                    plaintext: event.data.selection.plaintext,
                                },
                            },
                        ],
                    });
                    console.log('response', response);
                    return;
                }
                if (event.kind === 'current') {
                    console.log('current', event.data.content.title);
                    return;
                }
                // assertNever(event);
            },
        });

        const props = controller.useProps(context, data, {
            chat,
            async onGenerate() {
                console.log('auto generating reply');
                await chat.reload();
            },
            async onSubmit(values) {
                let context: ContextItem[] = [];
                if (containerProps.content) {
                    context.push({
                        kind: 'editing',
                        data: {
                            document: containerProps.content,
                        },
                    });
                }
                if (data.thread) {
                    context.push(
                        ...data.thread.context.flatMap((item): ContextItem[] => {
                            if (item.kind === 'reference' || item.kind === 'root') {
                                return [
                                    {
                                        kind: item.kind,
                                        data: {
                                            document: item.document,
                                        },
                                    },
                                ];
                            }
                            if (item.kind === 'prompt') {
                                return [
                                    {
                                        kind: 'prompt',
                                        data: {
                                            text: item.prompt,
                                        },
                                    },
                                ];
                            }
                            if (item.kind === 'task') {
                                return [
                                    {
                                        kind: 'task',
                                        data: item,
                                    },
                                ];
                            }
                            if (item.kind === 'template') {
                                return [
                                    {
                                        kind: 'template',
                                        data: item,
                                    },
                                ];
                            }
                            if (item.kind === 'collection') {
                                return [
                                    {
                                        kind: 'collection',
                                        data: item,
                                    },
                                ];
                            }
                            if (item.kind === 'file') {
                                return [
                                    {
                                        kind: 'file',
                                        data: item,
                                    },
                                ];
                            }
                            if (item.kind === 'resource') {
                                return [
                                    {
                                        kind: 'resource',
                                        data: item,
                                    },
                                ];
                            }
                            assertNever(item);
                        })
                    );
                }
                console.log('sending onSubmut', { text: values.text, context });
                const response = await chat.append({
                    parts: [
                        {
                            type: 'text',
                            text: values.text,
                        },
                    ],
                    context,
                });
            },
        });

        return <View {...props} />;
    });
}
